/**
 * JSON-safe value. Every protocol message body must be one of these so that the
 * same encoder works over a `MessagePort` (structured clone) and over the
 * WebSocket bridge that carries remote apps (§4.6). Capabilities are the sole
 * exception: they travel out-of-band in a transfer list, referenced from the
 * body by a `PortRef` marker.
 */
export type JsonValue =
  | null
  | boolean
  | number
  | string
  | readonly JsonValue[]
  | { readonly [key: string]: JsonValue };

export type JsonObject = { readonly [key: string]: JsonValue };

export class ProtocolError extends Error {
  readonly path: string;

  constructor(message: string, path: string) {
    super(path === "" ? message : `${path}: ${message}`);
    this.name = "ProtocolError";
    this.path = path;
  }
}

export function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

/**
 * Convert a value to its JSON-safe form, mirroring `JSON.stringify` semantics:
 * `undefined` object properties are dropped, `undefined` anywhere else is an
 * error. Anything that could not survive the WebSocket bridge is rejected here
 * rather than silently at the far end.
 */
export function toJson(value: unknown, path = ""): JsonValue {
  if (value === null) return null;
  switch (typeof value) {
    case "boolean":
    case "string":
      return value;
    case "number":
      if (!Number.isFinite(value)) {
        throw new ProtocolError(`non-finite number (${String(value)})`, path);
      }
      return value;
    case "object":
      break;
    default:
      throw new ProtocolError(`value of type ${typeof value} is not JSON-safe`, path);
  }

  if (Array.isArray(value)) {
    return value.map((item, i) => {
      if (item === undefined) {
        throw new ProtocolError("undefined array element", `${path}[${i}]`);
      }
      return toJson(item, `${path}[${i}]`);
    });
  }

  if (!isRecord(value)) {
    throw new ProtocolError("only plain objects and arrays are JSON-safe", path);
  }
  const proto: unknown = Object.getPrototypeOf(value);
  if (proto !== Object.prototype && proto !== null) {
    throw new ProtocolError("only plain objects and arrays are JSON-safe", path);
  }

  const out: Record<string, JsonValue> = {};
  for (const [key, item] of Object.entries(value)) {
    if (item === undefined) continue;
    out[key] = toJson(item, path === "" ? key : `${path}.${key}`);
  }
  return out;
}

export function expectRecord(value: unknown, path: string): Record<string, unknown> {
  if (!isRecord(value)) throw new ProtocolError("expected an object", path);
  return value;
}

export function expectString(value: unknown, path: string): string {
  if (typeof value !== "string") throw new ProtocolError("expected a string", path);
  return value;
}

export function expectNonEmptyString(value: unknown, path: string): string {
  const s = expectString(value, path);
  if (s === "") throw new ProtocolError("expected a non-empty string", path);
  return s;
}

export function expectNumber(value: unknown, path: string): number {
  if (typeof value !== "number" || !Number.isFinite(value)) {
    throw new ProtocolError("expected a finite number", path);
  }
  return value;
}

export function expectArray(value: unknown, path: string): unknown[] {
  if (!Array.isArray(value)) throw new ProtocolError("expected an array", path);
  return value;
}

export function expectOneOf<T extends string>(
  value: unknown,
  allowed: ReadonlySet<string>,
  path: string,
): T {
  const s = expectString(value, path);
  if (!allowed.has(s)) {
    throw new ProtocolError(`unknown value ${JSON.stringify(s)}`, path);
  }
  return s as T;
}
